/**
 * Component: sa-hero-banner
 * Description: Sticky scroll hero banner.
 * The section height is multiplied by the number of slides to create a scroll track.
 * As the user scrolls down the page (native scroll), the background image switches.
 * We use a larger height multiplier (e.g. 1.5x or 2x) to "hold" the user on each slide for longer.
 */
if (!customElements.get('sa-hero-banner')) {
  class SaHeroBanner extends HTMLElement {
    constructor() {
      super();
      this.handleScroll = this.handleScroll.bind(this);
      this.handleIntersection = this.handleIntersection.bind(this);
      this.handleClick = this.handleClick.bind(this);
      this.handleVideoEnded = this.handleVideoEnded.bind(this);
      
      this.currentSlideIndex = 0;
      // Scroll distance per slide (in vh)
      // 100vh = 1 screen height. 
      // Reduced from 150vh to 120vh for faster response/switching
      this.scrollPerSlide = 120; 
    }

    connectedCallback() {
      // Elements
      this.slider = this.querySelector('.sa-hero-banner__slider');
      this.slides = this.querySelectorAll('.sa-hero-banner__slide');
      this.totalSlides = this.slides.length;
      this.content = this.querySelector('.sa-hero-banner__content');
      this.btn = this.querySelector('.sa-hero-banner__btn');
      
      // Settings
      this.enableAnimation = this.getAttribute('data-enable-animation') === 'true';

      // Setup Section Height
      // If we have multiple slides, make the section tall to allow scrolling "through" it
      if (this.totalSlides > 1) {
        // Total height = (Number of slides * scrollPerSlide) + a bit of buffer
        // We set it in vh
        this.style.height = `${this.totalSlides * this.scrollPerSlide}vh`;
        window.addEventListener('scroll', this.handleScroll, { passive: true });
        
        // Trigger initial check in case we load in the middle of the section
        this.handleScroll();
      } else {
        this.style.height = '100vh';
      }

      // Initial Animation
      if (this.enableAnimation && this.content) {
        this.content.classList.add('sa-hero-banner__content--hidden');
        this.initIntersectionObserver();
      }

      if (this.btn) {
        this.btn.addEventListener('click', this.handleClick);
      }

      // Video Logic
      this.videoWrapper = this.querySelector('[data-video-wrapper]');
      if (this.videoWrapper) {
        const video = this.videoWrapper.querySelector('video');
        if (video) {
          // Fix for mobile autoplay: Ensure muted and playsinline are set
          video.muted = true;
          video.defaultMuted = true;
          video.playsInline = true;
          video.setAttribute('playsinline', '');
          video.setAttribute('muted', '');
          
          // Try to play if autoplay is set
          if (video.hasAttribute('autoplay')) {
             const playPromise = video.play();
             if (playPromise !== undefined) {
               playPromise.catch(error => {
                 console.log('Autoplay prevented by browser:', error);
               });
             }
          }

          video.addEventListener('ended', this.handleVideoEnded);
        }
      }
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.handleScroll);
      if (this.observer) {
        this.observer.disconnect();
      }
      if (this.btn) {
        this.btn.removeEventListener('click', this.handleClick);
      }
      
      if (this.videoWrapper) {
        const video = this.videoWrapper.querySelector('video');
        if (video) {
          video.removeEventListener('ended', this.handleVideoEnded);
        }
      }
    }

    handleVideoEnded() {
      if (this.videoWrapper) {
        this.videoWrapper.classList.add('hidden');
      }
    }

    initIntersectionObserver() {
      const options = {
        root: null,
        rootMargin: '0px',
        threshold: 0.1
      };

      this.observer = new IntersectionObserver(this.handleIntersection, options);
      if (this.content) {
        this.observer.observe(this.content);
      }
    }

    handleIntersection(entries) {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          this.content.classList.remove('sa-hero-banner__content--hidden');
          this.content.classList.add('sa-hero-banner__content--animate');
          this.observer.unobserve(entry.target);
        }
      });
    }

    handleScroll() {
      if (this.totalSlides <= 1) return;

      const rect = this.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      
      // Calculate how far we've scrolled into the section (in pixels)
      // rect.top is negative as we scroll down
      const scrolled = -rect.top;
      
      // If we are above the section, reset to 0
      if (scrolled < 0) {
        this.updateSlide(0);
        return;
      }
      
      // If we are past the section, keep at last slide
      const totalHeight = this.offsetHeight;
      if (scrolled > totalHeight) {
        this.updateSlide(this.totalSlides - 1);
        return;
      }

      // Calculate index
      // We map the scrolled pixels to the "scrollPerSlide" ratio
      const scrollPerSlidePx = viewportHeight * (this.scrollPerSlide / 100);
      
      let index = Math.floor(scrolled / scrollPerSlidePx);

      // Clamp index
      if (index < 0) index = 0;
      if (index >= this.totalSlides) index = this.totalSlides - 1;

      this.updateSlide(index);
    }

    updateSlide(index) {
      if (this.currentSlideIndex === index) return;
      this.currentSlideIndex = index;

      if (this.slider) {
        this.slider.style.transform = `translateY(-${index * 100}vh)`;
      }
    }

    handleClick(e) {
      // console.log('Button clicked');
    }
  }

  customElements.define('sa-hero-banner', SaHeroBanner);
}
